import * as React from 'react';
import * as ToolbarPrimitive from '@radix-ui/react-toolbar';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cva } from 'class-variance-authority';
import { ChevronDown } from 'lucide-react';
import {
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuSeparator,
} from 'src/components/ui/editor/ui/dropdown-menu';
import { Separator } from 'src/components/ui/editor/ui/separator';
import {
  TooltipEditor,
  TooltipEditorProvider,
  TooltipEditorTrigger,
} from 'src/components/ui/editor/ui/tooltip-editor';
import { cn } from 'src/lib/utils';
import { forwardRef } from 'react';

export function ToolbarEditor({ className, ...props }) {
  return (
    <ToolbarPrimitive.Root
      className={cn('relative flex select-none items-center', className)}
      {...props}
    />
  );
}

export const ToolbarEditorToggleGroup = forwardRef(({ className, ...props }, ref) => {
  return (
    <ToolbarPrimitive.ToolbarToggleGroup
      ref={ref}
      className={cn('flex items-center', className)}
      {...props}
    />
  );
});

export function ToolbarEditorLink({ className, ...props }) {
  return (
    <ToolbarPrimitive.Link
      className={cn('font-medium underline underline-offset-4', className)}
      {...props}
    />
  );
}

export function ToolbarEditorSeparator({ className, ...props }) {
  return (
    <ToolbarPrimitive.Separator
      className={cn('mx-2 my-1 w-px shrink-0 bg-border', className)}
      {...props}
    />
  );
}

// From toggleVariants
const toolbarButtonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium text-sm outline-none transition-[color,box-shadow] hover:bg-muted hover:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-checked:bg-accent aria-checked:text-accent-foreground aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
    variants: {
      size: {
        default: 'h-9 min-w-9 px-2',
        lg: 'h-10 min-w-10 px-2.5',
        sm: 'h-8 min-w-8 px-1.5',
      },
      variant: {
        default: 'bg-transparent',
        outline:
          'border border-input bg-transparent shadow-xs hover:bg-accent hover:text-accent-foreground',
      },
    },
  },
);

const dropdownArrowVariants = cva(
  cn(
    'inline-flex items-center justify-center rounded-r-md font-medium text-foreground text-sm transition-colors disabled:pointer-events-none disabled:opacity-50',
  ),
  {
    defaultVariants: {
      size: 'sm',
      variant: 'default',
    },
    variants: {
      size: {
        default: 'h-9 w-6',
        lg: 'h-10 w-8',
        sm: 'h-8 w-4',
      },
      variant: {
        default:
          'bg-transparent hover:bg-muted hover:text-muted-foreground aria-checked:bg-accent aria-checked:text-accent-foreground',
        outline:
          'border border-input border-l-0 bg-transparent hover:bg-accent hover:text-accent-foreground',
      },
    },
  },
);

const ToolBarEditorButton = forwardRef(
  ({ children, className, isDropdown, pressed, size = 'sm', variant, ...props }, ref) => {
    return typeof pressed === 'boolean' ? (
      <ToolbarEditorToggleGroup ref={ref} disabled={props.disabled} value="single" type="single">
        <ToolbarEditorToggleItem
          className={cn(
            toolbarButtonVariants({
              size,
              variant,
            }),
            isDropdown && 'justify-between gap-1 pr-1',
            className,
          )}
          value={pressed ? 'single' : ''}
          {...props}
        >
          {isDropdown ? (
            <>
              <div className="flex flex-1 items-center gap-2 whitespace-nowrap">{children}</div>
              <div>
                <ChevronDown className="size-3.5 text-muted-foreground" data-icon />
              </div>
            </>
          ) : (
            children
          )}
        </ToolbarEditorToggleItem>
      </ToolbarEditorToggleGroup>
    ) : (
      <ToolbarPrimitive.Button
        ref={ref}
        className={cn(
          toolbarButtonVariants({
            size,
            variant,
          }),
          isDropdown && 'pr-1',
          className,
        )}
        {...props}
      >
        {children}
      </ToolbarPrimitive.Button>
    );
  },
);

export const ToolbarEditorButton = withTooltip(ToolBarEditorButton);
export const ToolbarEditorButtonWithoutTooltip = ToolBarEditorButton;

export function ToolbarEditorSplitButton({ className, ...props }) {
  return (
    <ToolbarEditorButtonWithoutTooltip
      className={cn('group flex gap-0 px-0 hover:bg-transparent', className)}
      {...props}
    />
  );
}

export function ToolbarEditorSplitButtonPrimary({
  children,
  className,
  size = 'sm',
  variant,
  ...props
}) {
  return (
    <span
      className={cn(
        toolbarButtonVariants({
          size,
          variant,
        }),
        'rounded-r-none',
        'group-data-[pressed=true]:bg-accent group-data-[pressed=true]:text-accent-foreground',
        className,
      )}
      {...props}
    >
      {children}
    </span>
  );
}

export const ToolbarEditorSplitButtonSecondary = forwardRef(
  ({ className, size, variant, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(
          dropdownArrowVariants({
            size,
            variant,
          }),
          'group-data-[pressed=true]:bg-accent group-data-[pressed=true]:text-accent-foreground',
          className,
        )}
        onClick={(e) => e.stopPropagation()}
        role="button"
        {...props}
      >
        <ChevronDown className="size-3.5 text-muted-foreground" data-icon />
      </span>
    );
  },
);

export function ToolbarEditorToggleItem({ className, size = 'sm', variant, ...props }) {
  return (
    <ToolbarPrimitive.ToggleItem
      className={cn(toolbarButtonVariants({ size, variant }), className)}
      {...props}
    />
  );
}

export function ToolbarEditorGroup({ children, className }) {
  return (
    <div className={cn('group/toolbar-group', 'relative hidden has-[button]:flex', className)}>
      <div className="flex items-center">{children}</div>
      <div className="group-last/toolbar-group:hidden! mx-1.5 py-0.5">
        <Separator className={'separator'} orientation="vertical" />
      </div>
    </div>
  );
}

function withTooltip(Component) {
  return function ExtendComponent({
    tooltip,
    tooltipContentProps,
    tooltipProps,
    tooltipTriggerProps,
    ...props
  }) {
    const [mounted, setMounted] = React.useState(false);

    React.useEffect(() => {
      setMounted(true);
    }, []);

    const component = <Component {...props} />;

    if (tooltip && mounted) {
      return (
        <TooltipEditorProvider>
          <TooltipEditor {...tooltipProps}>
            <TooltipEditorTrigger asChild {...tooltipTriggerProps}>
              {component}
            </TooltipEditorTrigger>
            <TooltipEditorContent {...tooltipContentProps}>{tooltip}</TooltipEditorContent>
          </TooltipEditor>
        </TooltipEditorProvider>
      );
    }

    return component;
  };
}

function TooltipEditorContent({
  children,
  className,

  // CHANGE
  sideOffset = 4,

  ...props
}) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        className={cn(
          'z-50 w-fit origin-(--radix-tooltip-content-transform-origin) text-balance rounded-md bg-primary px-3 py-1.5 text-primary-foreground text-xs',
          className,
        )}
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        {...props}
      >
        {children}
        {/* CHANGE */}
        {/* <TooltipPrimitive.Arrow className="z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] bg-primary fill-primary" /> */}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export function ToolbarEditorMenuGroup({ children, className, label, ...props }) {
  return (
    <>
      <DropdownMenuSeparator
        className={cn(
          'hidden',
          'mb-0 shrink-0 peer-has-[[role=menuitem]]/menu-group:block peer-has-[[role=menuitemradio]]/menu-group:block peer-has-[[role=option]]/menu-group:block',
        )}
      />
      <DropdownMenuRadioGroup
        {...props}
        className={cn(
          'hidden',
          'peer/menu-group group/menu-group my-1.5 has-[[role=menuitem]]:block has-[[role=menuitemradio]]:block has-[[role=option]]:block',
          className,
        )}
      >
        {label && (
          <DropdownMenuLabel className="select-none font-semibold text-muted-foreground text-xs">
            {label}
          </DropdownMenuLabel>
        )}
        {children}
      </DropdownMenuRadioGroup>
    </>
  );
}
